import React, {
  memo,
  useMemo,
  useEffect,
  useContext,
  useState,
  useCallback,
} from 'react';
import { H3 } from 'tea-component';
import { SortBy } from 'tea-component/lib/table/addons';
import TriggerViewCard from '@gp/components/TriggerViewCard';
import GPSelectMultiple from '@gp/components/SelectMultiple';
import GeneralDownloadBtn from '@gp/components/GeneralDownloadBtn';
import { FILE_TYPES2, FILE_TYPES_MAP, SELECT_PANEL_HEIGHT, CONSTANT_TEXTS } from '@gp/constants/const';
import { context as Context } from '@gp/components/Context';
import Table from './components/Table';
import { useRequest } from 'ahooks';
import {
  queryCostList,
  queryCostDownload,
} from '@gp/pages/StudioDashboard/StudioOverall/services/studioOverallDetail';
import {
  getDownLoadCSVResponseHeaderFileName,
  downloadFileByBlob,
  stream2Object,
  formatDateStr,
} from '@gp/utils/common';
import { reportOperationLog } from '@gp/services/global';
import { ALL_REPORT_OPERATION_LOG } from '@gp/constants/operationLogParams';
import classNames from 'classnames';
import './index.scss';
import CardSlot from '@/pages/globalpedia/components/CardSlot';

interface CostPropsType {
  className?: string;
  style?: React.CSSProperties;
  filterLoading: boolean;
  filterList: { id: string; name: string }[];
}

const DEFAULT_PROPS: CostPropsType = {
  className: '',
  style: {},
  filterLoading: true,
  filterList: [],
};

function Cost(props: CostPropsType) {
  const { className, style, filterLoading, filterList } = props;

  const [filterV, setFilterV] = useState<string[]>([]);
  // 控制是否请求接口，防止首次打开和关闭筛选器无操作，多余请求接口
  const [reqSwitch, setReqSwitch] = useState<boolean>(true);
  const [sortsParams, setSortsParams] = useState<SortBy<any>>({
    by: '',
    order: 'desc',
  });
  const [downloadFileType, setDownloadFileType] = useState<number>(2);

  const { studioId } =
    useContext<IStudioOverallProjectsDetailsContext>(Context) || {};

  const { loading, data, run } = useRequest(queryCostList, { manual: true });
  const updateTimeFormatted = formatDateStr((data as any)?.update_time, 'YYYY-MM');
  const {
    loading: downloadLoading,
    data: downloadData,
    run: runDownload,
  } = useRequest(queryCostDownload, { manual: true });

  const handleDownload = (payload: any) => {
    runDownload({
      studio_id: studioId,
      file_type: payload?.fileType === FILE_TYPES_MAP.CSV ? 1 : 2,
      sort_type: sortsParams?.order as 'asc' | 'desc',
      sort: sortsParams?.by,
      ids: filterV || [],
      stage: 'development',
    });
    setDownloadFileType(payload?.fileType || 2);
  };

  // 抛出下载后的文件
  const throwDownLoadResult = async () => {
    try {
      // 拿到响应头中的文件名称
      const fileName = getDownLoadCSVResponseHeaderFileName(
        downloadData?.headers,
      );
      downloadFileByBlob(downloadData?.data, fileName);
      reportOperationLog(
        ALL_REPORT_OPERATION_LOG[
          downloadFileType == FILE_TYPES_MAP.CSV
            ? 'STUDIO_OVERALL_DETAIL_PROJECTS_COST_CSV_SUBMIT'
            : 'STUDIO_OVERALL_DETAIL_PROJECTS_COST_EXCEL_SUBMIT'
        ],
        'button',
      );
    } catch (err: any) {
      const resData = await stream2Object(err?.data);
      throw new Error(resData?.msg || '');
    }
  };
  // 下载结果
  useEffect(() => {
    if (downloadLoading || !downloadData) return;
    throwDownLoadResult();
  }, [downloadLoading]);

  const options = useMemo(() => {
    if (!Array.isArray(filterList) || !filterList?.length) {
      return [];
    }
    const _options = filterList?.map((t: { id: string; name: string }) => {
      return {
        value: t?.id || '',
        label: t?.name || '-',
        text: t?.name || '-',
        groupKey: 'separated',
      };
    });

    return _options;
  }, [filterList]);

  const handleChange = useCallback(
    (v: string[]) => {
      // 首次打开筛选器且全选关闭筛选器时
      const flag = !filterV?.length && v?.length === options?.length;
      setReqSwitch(!flag);
      setFilterV(v ? [...v] : []);
    },
    [filterV, options],
  );

  // 筛选器变化调接口, 排序发生改变调用接口
  useEffect(() => {
    if (!reqSwitch) {
      setReqSwitch(true);
      return;
    }
    run({
      studio_id: studioId,
      ids: filterV || [],
      sort_type: sortsParams?.order as 'asc' | 'desc',
      sort: sortsParams?.by,
      stage: 'development',
    });
  }, [filterV, sortsParams?.by, sortsParams?.order]);

  // const tabs = useMemo(() => {
  //   return [
  //     {
  //       id: 'download',
  //       label: (
  //         <GeneralDownloadBtn
  //           uiType="2"
  //           fileTypes={FILE_TYPES2}
  //           isLoading={downloadLoading}
  //           onConfirm={handleDownload}
  //         />
  //       ),
  //       component: (
  //         <>
  //           <GPSelectMultiple
  //             className="gp-studio-overall-details-projects-dev-cost-project-selector"
  //             label="Project"
  //             listHeight={SELECT_PANEL_HEIGHT}
  //             staging={false}
  //             searchable
  //             clearable
  //             allOption={{
  //               text: 'All',
  //               value: 'all',
  //             }}
  //             groups={{ separated: CONSTANT_TEXTS.SELECT_SEPARATED_GROUP_NAME }}
  //             options={options}
  //             value={filterV}
  //             onChange={handleChange}
  //           />
  //           <Table
  //             loading={loading}
  //             data={data}
  //             setSortsParams={setSortsParams}
  //           />
  //         </>
  //       ),
  //     },
  //   ];
  // }, [downloadLoading, loading, options, filterV, sortsParams]);

  return (
    <CardSlot
      className={classNames({
        'dbg-studio-overall-details-projects-dev-cost': true,
        [className as string]: !!className,
      })}
      style={style}
      title={
        <div className="dbg-studio-overall-details-projects-dev-cost-title">
          Cost
          {!!updateTimeFormatted && <span className="dbg-studio-overall-details-projects-dev-cost-subtitle">Data Time: {updateTimeFormatted}</span>}
        </div>
      }
      loading={false}
      isNoData={false}
      rightDom={
        <GeneralDownloadBtn
          uiType="2"
          fileTypes={FILE_TYPES2}
          isLoading={downloadLoading}
          onConfirm={handleDownload}
        />
      }
    >
      <GPSelectMultiple
        className="dbg-studio-overall-details-projects-dev-cost-project-selector"
        label="Project"
        listHeight={SELECT_PANEL_HEIGHT}
        staging={false}
        searchable
        clearable
        disabled={filterLoading}
        allOption={{
          text: 'All',
          value: 'all',
        }}
        groups={{ separated: CONSTANT_TEXTS.SELECT_SEPARATED_GROUP_NAME }}
        options={options}
        value={filterV}
        onChange={handleChange}
      />
      <Table loading={loading} data={data} setSortsParams={setSortsParams} />
    </CardSlot>
  );
}

Cost.defaultProps = DEFAULT_PROPS;

export default memo(Cost);
